import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    FormControlLabel,
    Switch,
} from '@mui/material';
import {
    Save as SaveIcon,
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import customerService from '../../../services/customerService';

const CustomerForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const { t } = useTranslation();

    // Clients are shared by every store and the website, so there is no store field.
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        isActive: true,
    });

    // A client with a website login: its email is their login identity (verified
    // by its owner), so staff can't change it (the API ignores it too).
    const [isWebsiteClient, setIsWebsiteClient] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isEdit) {
            fetchCustomer();
        }
    }, [id]);

    const fetchCustomer = async () => {
        try {
            const data = await customerService.getCustomerById(id);
            setIsWebsiteClient(data.source === 'WEBSITE');
            setFormData({
                firstName: data.firstName || '',
                lastName: data.lastName || '',
                email: data.email || '',
                phone: data.phone || '',
                isActive: data.isActive !== undefined ? data.isActive : true,
            });
        } catch (error) {
            toast.error(t('customers.fetchError'));
            console.error(error);
            navigate('/customers');
        }
    };

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = t('validation.firstNameRequired');
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = t('validation.lastNameRequired');
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('validation.invalidEmail');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            toast.error(t('common.saveError'));
            return;
        }

        setLoading(true);
        try {
            if (isEdit) {
                await customerService.updateCustomer(id, formData);
                toast.success(t('customers.updateSuccess'));
            } else {
                await customerService.createCustomer(formData);
                toast.success(t('customers.createSuccess'));
            }
            navigate('/customers');
        } catch (error) {
            toast.error(
                error.response?.data?.message || t('common.saveError')
            );
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/customers')}>
                    {t('common.back')}
                </Button>
                <Typography variant="h4">
                    {isEdit ? t('customers.editCustomer') : t('customers.createCustomer')}
                </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Card>
                    <CardContent>
                        <Grid container spacing={3}>
                            {/* First Name */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label={t('customers.firstName')}
                                    value={formData.firstName}
                                    onChange={(e) => handleChange('firstName', e.target.value)}
                                    error={Boolean(errors.firstName)}
                                    helperText={errors.firstName}
                                />
                            </Grid>

                            {/* Last Name */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label={t('customers.lastName')}
                                    value={formData.lastName}
                                    onChange={(e) => handleChange('lastName', e.target.value)}
                                    error={Boolean(errors.lastName)}
                                    helperText={errors.lastName}
                                />
                            </Grid>

                            {/* Email */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('customers.email')}
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    disabled={isWebsiteClient}
                                    error={Boolean(errors.email)}
                                    helperText={errors.email || (isWebsiteClient ? t('customers.websiteEmailLocked') : undefined)}
                                />
                            </Grid>

                            {/* Phone */}
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('customers.phone')}
                                    value={formData.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                />
                            </Grid>

                            {/* Active Status */}
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isActive}
                                            onChange={(e) => handleChange('isActive', e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={t('common.active')}
                                />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ ml: 4 }}
                                >
                                    {t('common.inactive')}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Action Buttons */}
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={loading}
                    >
                        {loading ? t('common.save') + '...' : isEdit ? t('customers.editCustomer') : t('customers.createCustomer')}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/customers')}
                        disabled={loading}
                    >
                        {t('common.cancel')}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default CustomerForm;
